import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, User, Send, Sparkles } from 'lucide-react';

/**
 * Interactive Chat Preview - Simulated AI conversation
 * Full customer journey: Lead → Inquiry → Order → Shipping → Payment → Closing
 */
export const ChatPreview = ({ className = '' }) => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const chatContainerRef = useRef(null);

  const conversation = [
    // Initial greeting
    { type: 'user', text: 'Halo, mau tanya dong' },
    { type: 'ai', text: 'Halo kak! 👋 Selamat datang di TokoFashion. Ada yang bisa aku bantu hari ini?' },
    
    // Product inquiry
    { type: 'user', text: 'Kaos polos navy ada stok?' },
    { type: 'ai', text: 'Ada kak! Kaos Polos Navy ready stock ✅\n\n📦 Stok: 45 pcs\n💰 Harga: Rp 89.000\n📏 Size: S, M, L, XL\n\nMau order size apa kak? 😊' },
    
    // Order details
    { type: 'user', text: 'Size L, 2 pcs' },
    { type: 'ai', text: 'Siap kak! 🛒\n\n📝 Pesanan:\n• 2x Kaos Polos Navy (L)\n• Total: Rp 178.000\n\nKirim ke kota mana kak? 📍' },
    
    // Shipping location
    { type: 'user', text: 'Bandung' },
    { type: 'ai', text: 'Noted, kirim ke Bandung! 🏙️\n\nPilih kurir pengiriman:\n\n🚚 JNE REG - Rp 15.000 (2-3 hari)\n⚡ JNE YES - Rp 25.000 (1 hari)\n📦 SiCepat - Rp 12.000 (2-3 hari)\n🚀 AnterAja - Rp 14.000 (2-3 hari)\n\nMau pakai kurir yang mana kak?' },
    
    // Courier selection
    { type: 'user', text: 'JNE REG aja' },
    { type: 'ai', text: 'Oke, pakai JNE REG ya kak! ✅\n\n📋 Mohon lengkapi data pengiriman:\n\n1️⃣ Nama penerima\n2️⃣ Nomor HP\n3️⃣ Alamat lengkap di Bandung\n4️⃣ Email (untuk invoice)\n\nBisa langsung kirim datanya ya kak 🙏' },
    
    // Customer data
    { type: 'user', text: 'Budi Santoso\n081234567890\nJl. Braga No. 45, Sumur Bandung, Kota Bandung 40111\nbudi.santoso@email.com' },
    { type: 'ai', text: 'Data sudah dicatat kak! ✅\n\n📦 Ringkasan Pesanan:\n• 2x Kaos Polos Navy (L) - Rp 178.000\n• Ongkir JNE REG - Rp 15.000\n• Total: Rp 193.000\n\n📍 Dikirim ke:\nBudi Santoso\nJl. Braga No. 45, Sumur Bandung\nBandung 40111\n\n💳 Pilih metode pembayaran:\n\n1️⃣ Transfer Bank (BCA/Mandiri/BRI)\n2️⃣ QRIS / E-Wallet (OVO, GoPay, Dana)\n3️⃣ COD (Bayar di tempat)\n\nMau bayar pakai apa kak?' },
    
    // Payment selection
    { type: 'user', text: 'Transfer BCA' },
    { type: 'ai', text: '🎉 Pesanan Dikonfirmasi!\n\n📋 Order #TF-20260201-001\n\nSilakan transfer ke:\n🏦 BCA: 1234567890\na.n. TokoFashion\nJumlah: Rp 193.000\n\n⏰ Batas bayar: 24 jam\n\nSetelah transfer, kirim bukti bayar ke sini ya kak! Pesanan akan langsung diproses ✅\n\nTerima kasih sudah belanja di TokoFashion! 🙏💙' },
  ];

  // Auto-scroll to bottom when messages change or typing indicator appears
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages, isTyping]);

  useEffect(() => {
    if (currentStep >= conversation.length) {
      // Reset after 5 seconds to replay
      const resetTimer = setTimeout(() => {
        setMessages([]);
        setCurrentStep(0);
      }, 5000);
      return () => clearTimeout(resetTimer);
    }

    const timer = setTimeout(() => {
      const msg = conversation[currentStep];
      
      if (msg.type === 'ai') {
        setIsTyping(true);
        // Longer typing for longer messages
        const typingDelay = Math.min(1500 + (msg.text.length * 5), 2500);
        setTimeout(() => {
          setIsTyping(false);
          setMessages(prev => [...prev, msg]);
          setCurrentStep(prev => prev + 1);
        }, typingDelay);
      } else {
        setMessages(prev => [...prev, msg]);
        setCurrentStep(prev => prev + 1);
      }
    }, currentStep === 0 ? 1000 : 1800);

    return () => clearTimeout(timer);
  }, [currentStep]);

  return (
    <div className={`${className}`}>
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-slate-200 dark:border-zinc-800 overflow-hidden max-w-md mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center dark:bg-zinc-900">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Nerava AI Agent</div>
            <div className="flex items-center gap-1 text-green-300 text-xs">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Online 24/7
            </div>
          </div>
          <Sparkles className="w-5 h-5 text-yellow-300" />
        </div>

        {/* Chat Area - with ref for auto-scroll */}
        <div 
          ref={chatContainerRef}
          className="h-[350px] overflow-y-auto p-4 space-y-3 bg-slate-50 dark:bg-[#0A0A0A] scroll-smooth"
        >
          <AnimatePresence>
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.type === 'ai' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-2 flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${
                    msg.type === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white dark:bg-zinc-900 text-slate-700 dark:text-zinc-200 rounded-bl-md shadow-sm'
                  }`}
                >
                  {msg.text}
                </div>
                {msg.type === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-slate-300 dark:bg-zinc-700 flex items-center justify-center ml-2 flex-shrink-0">
                    <User className="w-4 h-4 text-slate-600 dark:text-zinc-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          
          {/* Typing Indicator */}
          <AnimatePresence>
            {isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-2"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white dark:bg-zinc-900 px-4 py-2.5 rounded-2xl rounded-bl-md shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Input Area (disabled) */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-zinc-800 flex items-center gap-2">
          <input
            type="text"
            placeholder="Ketik pesan..."
            disabled
            className="flex-1 bg-slate-100 dark:bg-zinc-800 rounded-full px-4 py-2 text-sm text-slate-500 cursor-not-allowed dark:text-zinc-400"
          />
          <button disabled className="p-2 bg-blue-600 rounded-full opacity-50 cursor-not-allowed">
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
      
      {/* Caption */}
      <p className="text-center text-sm text-slate-500 dark:text-zinc-400 mt-4">
        ✨ Demo: AI Agent handle customer dari inquiry hingga closing otomatis
      </p>
    </div>
  );
};

export default ChatPreview;
